import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../api.ts';
import { TopBar } from '../../components/TopBar.tsx';
import { TrustBadges } from '../../components/TrustBadges.tsx';
import { ProSummary } from '../../components/ProSummary.tsx';
import { RatingLine } from '../../components/RatingLine.tsx';
import { ReliabilityBadges } from '../../components/ReliabilityBadges.tsx';
import type { ExtraProfile } from '../../types.ts';
import { Avatar } from '../../components/Avatar.tsx';
import { openConversation } from '../../messages.ts';

export function ExtraProfileView() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const missionId = searchParams.get('missionId');
  const navigate = useNavigate();
  const [extra, setExtra] = useState<ExtraProfile | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ extra: ExtraProfile }>(`/extras/${id}`).then((res) => setExtra(res.extra));
  }, [id]);

  async function write() {
    if (!missionId || !id) return;
    setBusy(true);
    setError(null);
    try {
      navigate(`/resto/messages/${await openConversation(missionId, id)}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'REQUEST_FAILED');
    } finally {
      setBusy(false);
    }
  }

  async function book() {
    if (!missionId || !id) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/missions/${missionId}/assign`, { extraId: id });
      navigate(`/resto/missions/${missionId}/payment`);
    } catch (err) {
      setError(err instanceof ApiError ? err.code : 'REQUEST_FAILED');
    } finally {
      setBusy(false);
    }
  }

  if (!extra) {
    return (
      <div className="app-shell">
        <TopBar title="Profil" back />
        <div className="screen-content empty-state">Chargement…</div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      <TopBar title="Profil" back />
      <div className="screen-content">
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
            <Avatar avatar={extra.avatar} size={88} label={`${extra.first_name} ${extra.last_name}`} />
          </div>
          <div style={{ fontSize: 20, fontWeight: 800 }}>{extra.first_name} {extra.last_name}</div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, marginTop: 6 }}>
            <RatingLine rating={extra.rating} />
            <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>{extra.missions_completed} missions réalisées</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, marginTop: 10 }}>
            <ReliabilityBadges reliability={extra.reliability} />
            <TrustBadges badges={extra.badges} />
          </div>
        </div>

        {extra.bio && <div className="card">{extra.bio}</div>}

        <ProSummary pro={extra.pro} />

        {extra.skills.length > 0 && (
          <div className="card">
            <div style={{ fontWeight: 800, marginBottom: 10 }}>Compétences</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {extra.skills.map((s) => (
                <span key={s} className="badge badge-neutral">{s}</span>
              ))}
            </div>
          </div>
        )}

        {error && <div className="error-banner">{error}</div>}

        {missionId && (
          <>
            <button className="btn btn-primary" onClick={book} disabled={busy}>
              {busy ? 'Réservation…' : 'Réserver pour cette mission'}
            </button>
            <button className="btn btn-secondary" onClick={write} disabled={busy}>Écrire à cet extra</button>
          </>
        )}
      </div>
    </div>
  );
}
